import { Plus, X } from 'lucide-react';

import { QuestionStep } from '../QuestionStep';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { STEPS, type QuestionnaireAnswers } from '../questions';

interface InspirationStepProps {
  answers: QuestionnaireAnswers;
  onChange: (next: Partial<QuestionnaireAnswers>) => void;
}

const MAX = 3;

export function InspirationStep({ answers, onChange }: InspirationStepProps) {
  const step = STEPS.find((s) => s.id === 'inspiration')!;
  const links = answers.inspiration_links ?? [];

  const setAt = (i: number, v: string) => {
    const next = [...links];
    next[i] = v;
    onChange({ inspiration_links: next });
  };

  const removeAt = (i: number) => {
    onChange({ inspiration_links: links.filter((_, idx) => idx !== i) });
  };

  const add = () => {
    if (links.length >= MAX) return;
    onChange({ inspiration_links: [...links, ''] });
  };

  return (
    <QuestionStep step={step}>
      <div className="grid max-w-2xl gap-3">
        {links.length === 0 ? (
          <p className="text-sm italic text-graphite">No inspirations yet — that's fine, this step is optional.</p>
        ) : null}

        {links.map((link, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="font-mono text-xs text-graphite tabular-nums">
              0{i + 1}
            </span>
            <Input
              value={link}
              onChange={(e) => setAt(i, e.target.value)}
              placeholder="https://example.com"
              type="url"
              className="flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeAt(i)}
              aria-label={`Remove inspiration ${i + 1}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}

        {links.length < MAX ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={add}
            className="w-fit"
          >
            <Plus className="h-3.5 w-3.5" />
            Add a link
          </Button>
        ) : (
          <p className="font-mono text-[11px] text-graphite">
            <span className="text-ochre">¶</span>&nbsp;&nbsp;Maximum {MAX} links.
          </p>
        )}
      </div>
    </QuestionStep>
  );
}
